import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { setArMonthStyle } from "../lib/dates";
import { rememberDoor } from "../lib/door";
import { fetchMe, cachedMe, clearCachedMe, loginAccount, staffLogin, verifyOtp, registerAccount, studentLogin as apiStudentLogin, setToken, getToken, hasSessionCookie, setOffice, pickAccount, switchAccount as apiSwitchAccount } from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = logged out, object = logged in
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      const hasToken = !!getToken();
      // Was this device ever signed in? (token, session-cookie marker or cached profile). If not → signed out, no request.
      if (!hasToken && !hasSessionCookie() && !cachedMe()) { if (active) { setUser(false); setReady(true); } return; }
      // No stored token (storage evicted / cleared): the HttpOnly session cookie may still be valid — always let the server decide.
      const tryMe = async (attempt = 0, cookieOnly = !hasToken) => {
        try { const u = await fetchMe({ cookieOnly }); setArMonthStyle(u?.ar_months); if (active) setUser(u); }
        catch (e) {
          const st = e?.response?.status;
          // a real auth failure is a JSON 401/403 from OUR api (has `detail`); an HTML 403/401 from the edge/CDN/captive portal is transient
          const fromApi = e?.response?.data && typeof e.response.data === "object" && "detail" in e.response.data;
          if (st === 401 && fromApi && !cookieOnly) return tryMe(attempt, true);  // stored token stale → try the session cookie before giving up
          if ((st === 401 || st === 403) && fromApi) { setToken(null); clearCachedMe(); if (active) setUser(false); return; }
          if (attempt < 2) { await new Promise((r) => setTimeout(r, 1500 * (attempt + 1))); return tryMe(attempt + 1, cookieOnly); }
          // server hiccup / offline / redeploy: keep the session alive with the last known profile (only if we still hold a credential)
          const cached = getToken() || hasSessionCookie() ? cachedMe() : null;
          if (active) setUser(cached || false);
        }
      };
      try { await tryMe(); } finally { if (active) setReady(true); }
    })();
    return () => { active = false; };
  }, []);

  const login = useCallback(async (email, password, expectedRole) => { const u = await loginAccount(email, password, expectedRole); if (!u.choose && !u.otp) { setUser(u); rememberDoor(u); } return u; }, []);
  const loginStaff = useCallback(async (orgCode, username, password) => { const u = await staffLogin(orgCode, username, password); if (!u.choose && !u.otp) { setUser(u); rememberDoor(u); } return u; }, []);
  const pick = useCallback(async (pickerToken, userId) => { const u = await pickAccount(pickerToken, userId); if (!u.otp) { setUser(u); rememberDoor(u); } return u; }, []);
  const otp = useCallback(async (otpToken, code, trust) => { const u = await verifyOtp(otpToken, code, trust); setUser(u); rememberDoor(u); return u; }, []);
  const switchAccount = useCallback(async (userId) => { const u = await apiSwitchAccount(userId); setUser(u); return u; }, []);
  const register = useCallback(async (form) => { const u = await registerAccount(form); setUser(u); rememberDoor(u); return u; }, []);
  const studentLogin = useCallback(async (code, sid, pin) => { const u = await apiStudentLogin(code, sid, pin); setUser(u); rememberDoor(u); return u; }, []);
  const logout = useCallback(() => { setToken(null); setOffice(null); clearCachedMe(); setUser(false); }, []);
  const refreshUser = useCallback(async () => { try { const u = await fetchMe(); setUser(u); return u; } catch (e) { return null; } }, []);
  const role = user ? (user.role || "parent") : null;
  const locked = role === "parent" && !!user?.subscription?.locked;

  return (
    <AuthContext.Provider value={{ user, ready, isAuthed: !!user, role, caps: user?.caps || [], isStaff: ["trainer", "center_admin", "super_admin", "role_manager", "finance_manager", "org_manager", "org_finance", "org_teacher_manager", "org_admin_officer"].includes(role), locked, login, loginStaff, pick, otp, switchAccount, register, studentLogin, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
